/*
 * ****************************************************************************
 * Copyright (C) 2018-2018, All rights reserved.
 * Project: chessmate
 * Created On: March 23rd, 2018
 * Last Modified: March 23rd, 2018
 * ****************************************************************************
 */

import * as React from 'react';
import * as Mui from 'mui';
import Chessboard, { RenderPieceFunction } from 'components/board/chessboard';
import { ChessRenderInfo, Dimensions, ChessPiece } from 'components/board/chessTypes';
import FenParser from '@chess-fu/fen-parser';
import { PopupMenuItem } from 'components';

/** GameProps - Properties */
export interface SetupBoardProps {
  fen: string;
  className?: string;
  style?: React.CSSProperties;
  flipBoard?: boolean;
  boardSize?: Dimensions;
  renderPiece: RenderPieceFunction;
  onMovePiece?: (move: { from: string, to: string }) => void;
  onPlacePiece?: (square: string, piece: string | null) => void;
}

/** SetupBoardState - Properties */
export interface SetupBoardState {
  selection?: string;
  currentPiece?: string;
  menuPosition?: { left: number, top: number };
}

/** Game */
export class SetupBoard extends React.Component<SetupBoardProps, SetupBoardState> {
  constructor(props: SetupBoardProps, ctx: any) {
    super(props, ctx);
    this.state = {};
  }

  componentDidUpdate(prevProps: SetupBoardProps, prevState: SetupBoardState, snapshot: any) {
    if (prevProps.fen !== this.props.fen) {
      this.setState({ selection: undefined });
    }
  }

  readonly onShowMenu = (e: React.MouseEvent<any>) => {
    const data = e.currentTarget.dataset;
    const square: string = data.square;

    this.setState({ selection: square, menuPosition: { top: e.clientY, left: e.clientX } });
  }

  readonly onClickSquare = (e: React.MouseEvent<any>) => {
    const { selection } = this.state;

    const data = e.currentTarget.dataset;
    const square: string = data.square;
    const piece: string | undefined = data.piece;

    if (piece) {
      this.setState({ selection: square, currentPiece: piece, menuPosition: { top: e.clientY, left: e.clientX } });
    }
    else {
      this.setState({ selection: square, menuPosition: { top: e.clientY, left: e.clientX } });
    }
  }

  placePiece(piece: string | null) {
    const { selection } = this.state;
    if (selection && this.props.onPlacePiece) {
      this.props.onPlacePiece(selection, piece);
    }
    this.setState({ selection: undefined, menuPosition: undefined });
  }

  readonly getSquareProps = (squareData: ChessRenderInfo, props: React.HTMLProps<HTMLDivElement>): React.HTMLProps<HTMLDivElement> => {
    const classes: string[] = [];
    const { selection } = this.state;
    const other: any = {};

    const key = `${squareData.square.file}${squareData.square.rank}`;
    if (selection && key === selection) {
      classes.push('selected');
      other.id = 'selected_square';
    }

    return {
      ...props, ...other,
      className: [props.className, ...classes].filter(x => x).join(' '),
      onClick: this.onClickSquare,
      onContextMenu: this.onShowMenu
    };
  };

  readonly renderPiece = (info: ChessRenderInfo): React.ReactNode => {
    return this.props.renderPiece(info);
  };

  render() {
    const boardSize = this.props.boardSize || { width: 512, height: 512 };
    const { className, style } = this.props;
    // While the board doesn't technically use selection, the getSquareProps method does.
    const props: any = { selection: this.state.selection };

    const boardProps = { ...props.boardProps };
    if (className) {
      boardProps.className = `${className} ${boardProps.className}`;
    }
    if (style) {
      boardProps.style = { ...style, ...boardProps.className };
    }

    const fenInfo = new FenParser(this.props.fen);
    const disabled = {
      wk: fenInfo.hasPiece('K'),
      bk: fenInfo.hasPiece('k'),
    };

    return (
      <React.Fragment>
        <Chessboard
          id="board"
          boardProps={boardProps}
          flipBoard={this.props.flipBoard}
          fen={this.props.fen}
          renderPiece={this.renderPiece}
          squareProps={this.getSquareProps}
          {...props}
          {...boardSize}
        />
        <Mui.Popover
          classes={{ paper: 'select-piece-menu' }}
          anchorReference="anchorPosition"
          anchorPosition={this.state.menuPosition}
          // anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          open={!!this.state.menuPosition}
          onClose={e => this.setState({ menuPosition: undefined, selection: undefined })}
        >
          {'bw'.split('').map(color => (
            <div className="select-piece-color" key={color}>
              {
                'kqbnrp'.split('').map(type => (
                  <div className="select-piece" key={type}>
                    <Mui.IconButton onClick={() => this.placePiece(`${color}${type}`)} disabled={disabled[`${color}${type}`]}>
                      <div style={{ opacity: disabled[`${color}${type}`] ? 0.2 : 1 }}>
                        {this.props.renderPiece({
                          size: { width: 32, height: 32 },
                          piece: { color, type } as ChessPiece,
                          square: { rank: 1, file: 'a', color: 'dark' }
                        })}
                      </div>
                    </Mui.IconButton>
                  </div>
                ))
              }
            </div>
          ))}
          {!!this.state.currentPiece && (<div className="clear-piece">
            <Mui.Button color="secondary" className="clear-piece-button"
              onClick={() => this.placePiece(null)}
            ><Mui.Icon>clear</Mui.Icon>Remove</Mui.Button>
          </div>)}
        </Mui.Popover>
      </React.Fragment>
    );
  }

}

export default SetupBoard;



// WEBPACK FOOTER //
// ./src/views/setupGame/components/setupBoard.tsx